/**
 * brep-kontur — BREP-tabanlı CAM sınır çıkarma (Faz 1: sıralı boundary loop).
 *
 * Bir BREP face'inin üçgen bölgesinden SIRALI sınır loop(ları)nı çıkarır.
 * Mevcut findCoplanarVertices sırasız vertex çorbası veriyordu (offset için riskli);
 * bu modül welded + sıralı + delik-ayrık kontur üretir.
 *
 * Saf/yan-etkisiz, THREE'ye bağımsız (2D IXY). Çağıran 3D üçgenleri face düzlemine
 * projekte edip verir. Tamamen deterministik → unit test edilebilir.
 */

export interface IXY { x: number; y: number; }

/** 2D üçgen (face düzlemine projekte edilmiş) */
export type Ucgen2D = [IXY, IXY, IXY];

export interface IBoundaryLoop {
    noktalar: IXY[];   // sıralı, kapalı (ilk≠son), CCW normalize
    kapali: boolean;
    alan: number;      // işaretli alan (|alan| büyük = dış kontur)
}

const QUANT = 1e4; // 0.1µm welding çözünürlüğü (mm girişte)

function anahtar(p: IXY): string {
    return `${Math.round(p.x * QUANT)},${Math.round(p.y * QUANT)}`;
}

/** İşaretli poligon alanı (CCW>0) */
export function isaretliAlan(pts: IXY[]): number {
    let a = 0;
    for (let i = 0; i < pts.length; i++) {
        const p = pts[i], q = pts[(i + 1) % pts.length];
        a += p.x * q.y - q.x * p.y;
    }
    return a / 2;
}

/**
 * Üçgen bölgesinden sıralı sınır loop(ları) çıkarır.
 * Sınır kenarı = bölge içinde tam olarak BİR üçgende görünen kenar.
 * Loop'lar bağlantı grafiği üzerinden traverse edilir; CCW'ye normalize edilir.
 */
export function sinirLoopCikar(ucgenler: Ucgen2D[]): IBoundaryLoop[] {
    if (!ucgenler.length) return [];

    // 1) Vertex welding → benzersiz nokta id'leri
    const idMap = new Map<string, number>();
    const noktalar: IXY[] = [];
    const idAl = (p: IXY): number => {
        const k = anahtar(p);
        let id = idMap.get(k);
        if (id === undefined) { id = noktalar.length; idMap.set(k, id); noktalar.push({ x: p.x, y: p.y }); }
        return id;
    };

    // 2) Kenar sayımı (yönsüz)
    const kenarSay = new Map<string, { a: number; b: number; sayi: number }>();
    const kenarEkle = (a: number, b: number) => {
        const lo = Math.min(a, b), hi = Math.max(a, b);
        const k = `${lo}_${hi}`;
        const e = kenarSay.get(k);
        if (e) e.sayi++; else kenarSay.set(k, { a: lo, b: hi, sayi: 1 });
    };
    for (const t of ucgenler) {
        const i0 = idAl(t[0]), i1 = idAl(t[1]), i2 = idAl(t[2]);
        if (i0 === i1 || i1 === i2 || i0 === i2) continue; // dejenere üçgen
        kenarEkle(i0, i1); kenarEkle(i1, i2); kenarEkle(i2, i0);
    }

    // 3) Sınır kenarları (sayi===1) → komşuluk
    const komsu = new Map<number, number[]>();
    const ekleKomsu = (a: number, b: number) => {
        if (!komsu.has(a)) komsu.set(a, []);
        komsu.get(a)!.push(b);
    };
    let sinirKenarSayisi = 0;
    for (const e of kenarSay.values()) {
        if (e.sayi === 1) { ekleKomsu(e.a, e.b); ekleKomsu(e.b, e.a); sinirKenarSayisi++; }
    }
    if (sinirKenarSayisi === 0) return [];

    // 4) Loop traverse (kullanılan kenarları işaretle)
    const kenarKullanildi = new Set<string>();
    const kenarAnahtar = (a: number, b: number) => `${Math.min(a, b)}_${Math.max(a, b)}`;
    const loops: IBoundaryLoop[] = [];

    for (const [bas] of komsu) {
        // bu düğümden başlayan kullanılmamış sınır kenarı var mı
        for (const ilkKomsu of komsu.get(bas)!) {
            if (kenarKullanildi.has(kenarAnahtar(bas, ilkKomsu))) continue;

            const loopIdx: number[] = [bas];
            let onceki = bas, mevcut = ilkKomsu;
            kenarKullanildi.add(kenarAnahtar(bas, ilkKomsu));
            let guvenlik = 0;

            while (mevcut !== bas && guvenlik++ < 1e6) {
                loopIdx.push(mevcut);
                const adaylar = (komsu.get(mevcut) || []).filter(
                    n => n !== onceki && !kenarKullanildi.has(kenarAnahtar(mevcut, n))
                );
                if (adaylar.length === 0) break; // açık zincir
                const sonraki = adaylar[0];
                kenarKullanildi.add(kenarAnahtar(mevcut, sonraki));
                onceki = mevcut; mevcut = sonraki;
            }

            if (loopIdx.length >= 3 && mevcut === bas) {
                const pts = loopIdx.map(i => noktalar[i]);
                let alan = isaretliAlan(pts);
                if (alan < 0) { pts.reverse(); alan = -alan; } // CCW normalize
                loops.push({ noktalar: pts, kapali: true, alan });
            }
        }
    }

    // En büyük alanlı loop ilk (dış kontur), delikler sonra
    loops.sort((a, b) => b.alan - a.alan);
    return loops;
}

/** Noktanın a-b çizgisine dik uzaklığı */
export function noktaCizgiUzaklik(p: IXY, a: IXY, b: IXY): number {
    const dx = b.x - a.x, dy = b.y - a.y;
    const L = Math.hypot(dx, dy);
    if (L < 1e-12) return Math.hypot(p.x - a.x, p.y - a.y);
    return Math.abs((p.x - a.x) * dy - (p.y - a.y) * dx) / L;
}

/**
 * Kapalı konturdan eşdoğrusal (collinear) ara noktaları temizler.
 * Tessellated düz kenar (çok nokta) → köşe noktaları. Yaylar korunur
 * (kiriş sapması tol'dan büyük). G-code nokta sayısını azaltır.
 */
export function konturSadelestir(pts: IXY[], tol = 1e-3): IXY[] {
    const n = pts.length;
    if (n < 3) return pts.slice();
    const tut: boolean[] = new Array(n).fill(true);
    for (let i = 0; i < n; i++) {
        const prev = pts[(i - 1 + n) % n], cur = pts[i], next = pts[(i + 1) % n];
        if (noktaCizgiUzaklik(cur, prev, next) < tol) tut[i] = false;
    }
    const out = pts.filter((_, i) => tut[i]);
    return out.length >= 3 ? out : pts.slice();
}

/** Düzlem tabanı: normal + nokta → (u,v) 2D projeksiyon fonksiyonu */
export function duzlemTabani(normal: { x: number; y: number; z: number }) {
    const n = normalize3(normal);
    const yardim = Math.abs(n.y) < 0.99 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
    const u = normalize3(cross3(yardim, n));
    const v = cross3(n, u);
    return { u, v, n };
}

function cross3(a: any, b: any) { return { x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x }; }
function normalize3(a: any) { const l = Math.hypot(a.x, a.y, a.z) || 1; return { x: a.x / l, y: a.y / l, z: a.z / l }; }
